import { AnalyticsBrowser } from "@segment/analytics-next";
import { KnownProjectIds, User as MadSDKUser } from "@madhive/mad-sdk";
import { Event, IListener, IUser } from "@madhive/mad-tracker";
import { firebaseProjectId } from "lib/sdk";
import { REACT_APP_GIT_COMMIT } from "lib/viteEnvConfig";
import { SegmentProjectID } from "./constants";
import { madTracker } from "../..";
import { RollbarPayloadOptions } from "./types";
import { isAiModePath } from "frontier/lib/ai-mode/utils";

/**
 * Rollbar payload for the current user: the name as `person.username` and the
 * org name as custom data, the fields Rollbar's item search can filter on.
 * Blank values are `null` because Rollbar's configure merge ignores `undefined`,
 * which would keep the previous user's values after switching accounts.
 */
export const getRollbarUserPayload = (
  traits: Partial<MadSDKUser> | undefined
): RollbarPayloadOptions => ({
  person: { username: traits?.name || null },
  custom: { orgName: traits?.primaryOrganizationName || null }
});

/**
 * Rollbar payload that tags errors with the build commit, matching the
 * source maps the deploy uploads under that commit.
 */
export const getRollbarCodeVersionPayload = (
  commit: string | undefined
): RollbarPayloadOptions =>
  commit
    ? {
        code_version: commit,
        client: { javascript: { code_version: commit } }
      }
    : {};

export class Segment implements IListener<MadSDKUser> {
  analytics = new AnalyticsBrowser();

  constructor() {
    let key;
    switch (firebaseProjectId) {
      case KnownProjectIds.MAD_MASTER:
        key = SegmentProjectID.PRODUCTION;
        break;
      case KnownProjectIds.MAD_STAGING:
        key = SegmentProjectID.STAGING;
        break;
      case KnownProjectIds.MAD_HACK:
        key = SegmentProjectID.DEVELOP;
        break;
      default:
        key = undefined;
        break;
    }

    if (key) {
      this.analytics.load({
        writeKey: key
      });

      this.analytics.ready(this.ready);
    }
  }

  user = {
    set: (user: IUser<MadSDKUser>) => {
      if (!user.traits) {
        return;
      }

      const {
        id,
        email,
        name,
        type,
        userType,
        isAdmin,
        isDev,
        primaryOrganizationId,
        primaryOrganizationName,
        brandingId,
        advertiserId,
        agencyId,
        teamId
      } = user.traits;

      this.analytics.identify(user.id, {
        id,
        email,
        name,
        type,
        userType,
        isAdmin,
        isDev,
        primaryOrganizationId,
        primaryOrganizationName,
        brandingId,
        advertiserId,
        agencyId,
        teamId,
        fullStorySession: window.FS?.getCurrentSession?.(),
        fullStorySessionUrl: window.FS?.getCurrentSessionURL?.(true)
      });

      if (user.traits?.primaryOrganizationId) {
        this.analytics.group(
          user.traits.primaryOrganizationId,
          user.traits || {}
        );
      }

      window.Rollbar?.configure?.({
        payload: getRollbarUserPayload(user.traits)
      });
    },
    reset: () => {
      this.analytics.reset();
      window.Rollbar?.configure?.({
        payload: getRollbarUserPayload(undefined)
      });
    }
  };

  track = ({
    event,
    properties
  }: {
    event: Event;
    properties?: Record<string, unknown>;
  }) => {
    // Attach the user and org information.
    const user = madTracker.user.get();
    const coreProperties = madTracker.core;
    const props: Record<string, unknown> = {
      ...properties,
      userId: user?.id,
      orgId: user?.traits?.primaryOrganizationId,
      orgName: user?.traits?.primaryOrganizationName,
      isAiMode: isAiModePath(),
      ...(coreProperties || {})
    };

    this.analytics.track(event, props);
  };

  page = () => {
    const user = madTracker.user.get();
    const coreProperties = madTracker.core;
    const properties: Record<string, any> = {
      userId: user?.id,
      orgId: user?.traits?.primaryOrganizationId,
      orgName: user?.traits?.primaryOrganizationName,
      ...(coreProperties || {})
    };
    this.analytics.page(undefined, undefined, properties);
  };

  ready = () => {
    // After Segment loads Rollbar, configure standard subdomains for Rollbar errors.
    this.analytics.ready(() => {
      if (!window.Rollbar?.configure) return;

      const subdomain =
        firebaseProjectId === KnownProjectIds.MAD_MASTER
          ? "app"
          : firebaseProjectId.split("-")?.[1] || KnownProjectIds.MAD_HACK;

      window.Rollbar.configure({
        payload: {
          ...getRollbarCodeVersionPayload(REACT_APP_GIT_COMMIT),
          ...getRollbarUserPayload(madTracker.user.get()?.traits)
        },
        // This mutates the payload in place (https://docs.rollbar.com/docs/source-maps#using-source-maps-on-many-domains)
        transform: payload => {
          payload?.body?.trace?.frames?.forEach(frame => {
            frame.filename = frame.filename?.replace?.(
              /https?:\/\/[^/]+?\//,
              `https://${subdomain}.madhive.com/`
            );
          });
        },
        checkIgnore: (_isUncaught, _args, payload) =>
          !!payload?.body?.trace?.frames?.some(frame =>
            frame.filename.startsWith("chrome-extension://")
          ),
        // Remove Firebase Snapshot listener spam.
        filterTelemetry: e =>
          !!e?.body?.url?.includes(
            "https://firestore.googleapis.com/google.firestore.v1.Firestore/Listen/channel"
          )
      });
    });
  };
}
